import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { API } from 'constants';
import cycleClient from '../../services/cycle-api';
import libraryClient from '../../services/library-api';

import { SORT_TYPES } from '../../constants/sort';
import { CONFIGURE_TAB } from '../../constants/configure';
import ConfigurePageHeader, { tabs } from './ConfigurePageHeader/ConfigurePageHeader';

import useDocumentTitle from '../../hooks/useDocumentTitle';
import useCustomPagination from '../../hooks/useCustomPagination';
import { useDefaultSearchParams } from '../../hooks/useDefaultSearchParams';
import { useSaveInContextRequestKeysWithMultipleAccessors } from '../../hooks/useSaveInContextRequestKeysWithMultipleAccessors';

import PlaybookDetailsModal from '../PlaybookBuilderPage/PlaybookDetailsModal/PlaybookDetailsModal';
import ConfigurePaginatedContainer from './ConfigurePaginatedContainer/ConfigurePaginatedContainer';
import CreateCycleModal from './CreateCycleModal/CreateCycleModal';

const route = {
    [CONFIGURE_TAB.processes]: API.ROUTES.library.process,
    [CONFIGURE_TAB.playbooks]: API.ROUTES.library.playbook,
    [CONFIGURE_TAB.cycles]: API.ROUTES.cycle.cycle,
};
const client = {
    [CONFIGURE_TAB.processes]: libraryClient,
    [CONFIGURE_TAB.playbooks]: libraryClient,
    [CONFIGURE_TAB.cycles]: cycleClient,
};
const requestSearchParams = {
    [CONFIGURE_TAB.processes]: { ordering: SORT_TYPES.created, shared: true },
    [CONFIGURE_TAB.playbooks]: { ordering: SORT_TYPES.alphabetical, shared: true },
    [CONFIGURE_TAB.cycles]: { ordering: SORT_TYPES.created },
};
const defaultRequestSearchParams = { ordering: SORT_TYPES.created, shared: true };
const DEFAULT_TAB = CONFIGURE_TAB.processes;
const DEFAULT_TAB_ROUTE = route[DEFAULT_TAB];

const ConfigurePage = () => {
    const [searchParams] = useSearchParams();
    const page = Number.parseInt(searchParams.get('page'));

    const activeTab = searchParams.get('tab');
    const activeTabIndex = Math.max(tabs.indexOf(activeTab), 0);

    const inactiveTabs = tabs.filter((tab) => tab !== activeTab);

    const [createModal, setCreateModal] = useState({ type: null });

    useDocumentTitle('Configure');

    const configureDataHookResponse = useCustomPagination({
        pageIndex: page - 1 || 0,
        client: client[activeTab] || libraryClient,
        searchParams: requestSearchParams[activeTab] || defaultRequestSearchParams,
        route: route[activeTab] || DEFAULT_TAB_ROUTE,
    });
    const { key, nextPageKey } = configureDataHookResponse;

    const requestKeys = useSaveInContextRequestKeysWithMultipleAccessors({
        key,
        nextPageKey,
        pageAccessor: activeTab || DEFAULT_TAB,
        allPageAccessors: tabs,
    });

    useDefaultSearchParams({
        checkPage: true,
        checkSort: false,
        checkTab: true,
        defaultTab: DEFAULT_TAB,
        availableTabs: tabs,
    });

    // cache data for inactive tab
    useCustomPagination({
        pageIndex: 0,
        searchParams: requestSearchParams[inactiveTabs[0]],
        client: client[inactiveTabs[0]],
        route: route[inactiveTabs[0]],
    });

    useCustomPagination({
        pageIndex: 0,
        searchParams: requestSearchParams[inactiveTabs[1]],
        client: client[inactiveTabs[1]],
        route: route[inactiveTabs[1]],
    });

    const closeCreateModal = () => setCreateModal({ type: null });

    return (
        <div className="page-position bg-neutral-50 overflow-y-auto p-4 xs:p-5 sm:p-8 flex flex-col gap-5">
            <ConfigurePageHeader activeTabIndex={activeTabIndex} setCreateModal={setCreateModal} />

            <ConfigurePaginatedContainer
                activeTab={activeTab}
                configureDataHookResponse={configureDataHookResponse}
                keysToMutate={requestKeys}
                setCreateModal={setCreateModal}
            />

            {createModal.type === 'playbook' && (
                <PlaybookDetailsModal
                    mode="create"
                    currentKey={key}
                    requestKeysToMutate={requestKeys?.playbooks}
                    onClose={closeCreateModal}
                />
            )}

            {createModal.type === 'cycle' && (
                <CreateCycleModal
                    currentKey={key}
                    requestKeysToMutate={requestKeys?.cycles}
                    onClose={closeCreateModal}
                />
            )}
        </div>
    );
};

export default ConfigurePage;
